/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import ConfirmationModal from '../../../components/modal/confirmationModal';
import Search from '../../../components/search/search';
import Table from '../../../components/table';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { DepartmentHeader } from '../../../constants/table-headers/departmentHeader';
import {
  useBoolean,
  useGetDepartmentListWithPagination,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import {
  CustomerDepartmentService,
  type Wealthlane_CustomerService_Dtos_CustomerDepartment_CustomerDepartmentDto,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import { PageHeader } from '../company-profile/profile.styled';
import { SettingRoleButton } from '../system-roles/systemRoleStyled';
import DepartmentModal from './departmentModal';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';

const departmentState = {
  data: InitialValuesForm.departmentForm,
  id: '',
  modalStatus: 'Add' as ModalStatusType,
  loading: false,
  searchQuery: '',
};

const DepartmentComponent = (): JSX.Element => {
  const [departmentId, setDepartmentId] = useState('');
  const [departmentInfo, setDepartmentInfo] = useState(departmentState);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmModalOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);

  // GET LIST
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneCustomerApi();
  const {
    data: departmentLists,
    isLoading,
    status,
  } = useGetDepartmentListWithPagination(handleRequest, tableInfo);
  const data = React.useMemo(() => departmentLists?.items || [], [departmentLists, isLoading]);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'DEPARTMENTS_TABLE_LIST',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  // TABLE ACTION
  const tableActions = [
    {
      id: 'view',
      action: 'View',
      onClick: (id: string) => {
        getDepartmentData(id, 'View');
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string) => {
        getDepartmentData(id, 'Edit');
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      loading: departmentInfo.loading,
      onClick: (id: string) => {
        setDepartmentId(id);
        openConfirmModal();
      },
    },
  ];

  const getDepartmentData = (id: string, type: ModalStatusType): any => {
    setDepartmentInfo((prevState) => ({
      ...prevState,
      loading: true,
    }));
    openModal();
    handleRequest(
      CustomerDepartmentService.getApiCustomerApiAppCustomerDepartmentCustomerDepartment(id)
    )
      .then((data?: Wealthlane_CustomerService_Dtos_CustomerDepartment_CustomerDepartmentDto) => {
        if (data != null) {
          setDepartmentInfo({
            ...departmentInfo,
            modalStatus: type,
            id,
            data: {
              name: data.name ?? '',
              description: data.description ?? '',
            },
            loading: false,
          });
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setDepartmentInfo((prevState) => ({
          ...prevState,
          loading: false,
        }));
      });
  };

  const deleteDepartmentData = (): void => {
    setDepartmentInfo({ ...departmentInfo, loading: true });
    handleRequest(
      CustomerDepartmentService.deleteApiCustomerApiAppCustomerDepartmentCustomerDepartment(
        departmentId
      )
    )
      .then((data?) => {
        if (data != null) {
          setDepartmentInfo({ ...departmentInfo, loading: false });
          toast.success(<ToastComponent label='Success' message='Department Deleted.' />);
          void queryClient.invalidateQueries('getDepartmentListWithPagination');
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      })
      .finally(() => {
        setDepartmentInfo({ ...departmentInfo, loading: false });
        closeConfirmModal();
      });
  };

  const schema = React.useMemo(
    () => ({
      columns: DepartmentHeader({ actions: schemaData?.columns, tableActions }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const renderRightToolbarButton = () => {
    return (
      <SettingRoleButton className='lg:col-start-10 lg:col-span-3 col-span-6'>
        <Button
          as='Create'
          variant={'primary'}
          title={'Add Department'}
          icon={'add_circle_outline'}
          id='addDepartment'
          onClick={() => {
            setDepartmentInfo({
              ...departmentInfo,
              data: InitialValuesForm.departmentForm,
              modalStatus: 'Add',
              id: '',
            });
            openModal();
          }}
        />
      </SettingRoleButton>
    );
  };

  return (
    <>
      <PageHeader>
        <Heading variant='h3' title='Departments' id='pageTitle' />
        <Heading
          variant='body-s'
          title={`Current list of Wealthlane system access department for`}
        />
      </PageHeader>
      <div className='grid sm:grid-cols-12 justify-between gap-4 sm:gap-0'>
        {/* <div className='lg:col-span-3 col-span-6'>
          <Search
            placeholder='Search'
            searchValue={departmentInfo.searchQuery}
            onSearch={(value: string) => {
              setDepartmentInfo({
                ...departmentInfo,
                searchQuery: value,
              });
            }}
            setTableInfo={setTableInfo}
            tableInfo={tableInfo}
          />
        </div> */}
        {/* <SettingRoleButton className='lg:col-start-10 lg:col-span-3 col-span-6'>
          <Button
            as='Create'
            variant={'primary'}
            title={'Add Department'}
            icon={'add_circle_outline'}
            onClick={() => {
              setDepartmentInfo({
                ...departmentInfo,
                data: InitialValuesForm.departmentForm,
                modalStatus: 'Add',
                id: '',
              });
              openModal();
            }}
          />
        </SettingRoleButton> */}
        <AnimationWrapper>
          {isModalOpen && (
            <DepartmentModal
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              initialData={departmentInfo}
              handleRequest={handleRequest}
              setModalStatus={(value) => {
                setDepartmentInfo({
                  ...departmentInfo,
                  modalStatus: value,
                });
              }}
              loading={departmentInfo.loading}
              id='departmemtModal'
            />
          )}

          {isConfirmModalOpen && (
            <ConfirmationModal
              isShow={isConfirmModalOpen}
              handleClose={() => {
                closeConfirmModal();
              }}
              title='department'
              handleSubmit={() => {
                deleteDepartmentData();
              }}
              loading={departmentInfo.loading}
            />
          )}
        </AnimationWrapper>
      </div>

      {/* <Table
        columns={DepartmentHeader(actions)}
        data={data?.items ?? []}
        loading={status}
        showPagination
        totalCounts={data?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
      <DynamicTable
        id="dataTable"
        schema={schema}
        data={data}
        totalCounts={departmentLists?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
      />
    </>
  );
};

export default withDynamicTableContext(DepartmentComponent, false, axiosInstance);
